import { runEndpoint } from '@/services/flow';
import { toast } from 'sonner';
import { fetchData } from '@/fetchapi/fetchapi';

/**
 * Get signed URL for an S3 file
 * @param {string} fileKey - S3 file key
 * @param {string} [bucket] - S3 bucket name (optional, uses default if not provided)
 * @returns {Promise<string>} - Signed URL
 */
export const getSignedUrl = async (fileKey, bucket) => {
  const params = { key: fileKey };
  if (bucket) params.bucket = bucket;
  const response = await runEndpoint('cortex.vdd.s3.get_datasource_signed_url_get', params);

  if (response && response.url) {
    return response.url;
  } else {
    throw new Error('No URL received');
  }
};

/**
 * Download a file from S3 using signed URL
 * @param {string} fileKey - S3 file key
 * @param {string} fileName - Name for the downloaded file
 * @param {string} [bucket] - S3 bucket name (optional)
 */
export const downloadFile = async (fileKey, fileName, bucket) => {
  try {
    const url = await getSignedUrl(fileKey, bucket);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || fileKey.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Download started');
  } catch (error) {
    console.error('Error downloading file:', error);
    toast.error('Failed to download file');
    throw error;
  }
};

/**
 * Fetch file content as ArrayBuffer from S3 using signed URL
 * Uses backend proxy to avoid CORS issues
 * @param {string} fileKey - S3 file key
 * @param {string} [bucket] - S3 bucket name (optional)
 * @returns {Promise<ArrayBuffer>} - File content as ArrayBuffer
 */
export const fetchFileAsArrayBuffer = async (fileKey, bucket) => {
  try {
    // Get signed URL
    const signedUrl = await getSignedUrl(fileKey, bucket);

    // Use backend proxy to fetch the file (avoids CORS issues)
    // The proxy endpoint will fetch from S3 and return the file
    return await fetchData(`proxy-s3?url=${encodeURIComponent(signedUrl)}`, {
      method: 'GET',
      responseType: 'arrayBuffer'
    });
  } catch (error) {
    console.error('Error fetching file:', error);
    throw error;
  }
};
